// src/utils/geo.js
// Shared geo helpers used by the maps page and the location picker.

const NOMINATIM_REVERSE_URL = 'https://nominatim.openstreetmap.org/reverse';

/**
 * Turn GPS coordinates into a readable place name via Nominatim reverse
 * geocoding. Falls back to the coordinates as a readable string on failure.
 * @param {number} lat
 * @param {number} lon
 * @returns {Promise<string>}
 */
export async function reverseGeocode(lat, lon) {
  try {
    const params = new URLSearchParams({ format: 'json', lat: String(lat), lon: String(lon) });
    const res = await fetch(`${NOMINATIM_REVERSE_URL}?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Reverse geocoding failed');
    const data = await res.json();
    return data?.display_name || `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
  } catch (e) {
    return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
  }
}

/**
 * Promise wrapper around navigator.geolocation.getCurrentPosition.
 * @param {Object} options geolocation options (enableHighAccuracy, timeout, maximumAge)
 * @returns {Promise<GeolocationPosition>}
 */
export function getCurrentPosition(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
      ...options,
    });
  });
}

/**
 * Get the user's position, retrying once with default accuracy if the
 * high-accuracy fix fails (some browsers/hardware reject enableHighAccuracy).
 * @returns {Promise<GeolocationPosition>}
 */
export async function getPositionWithRetry() {
  try {
    return await getCurrentPosition({ enableHighAccuracy: true });
  } catch (firstError) {
    return await getCurrentPosition({ enableHighAccuracy: false, timeout: 10000 });
  }
}
